import { domainToChainId, raise } from '@sparkdotfi/common-universal'
import { notFound, useParams } from '@tanstack/react-router'
import { useChainId, useChains } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'

export interface UseSelectedAccountResult {
  savingsTokenSymbol: TokenSymbol
  chainId: number
}

export function useSelectedAccount(): UseSelectedAccountResult {
  const { chainId } = usePageChainId()
  const wagmiChainId = useChainId()
  const chains = useChains()
  const { savingsToken: savingsTokenFromUrl, domain: domainFromUrl } = useParams({
    strict: false,
  })
  const { isInSandbox } = useSandbox()
  const chainIdFromUrl: number | undefined = isInSandbox
    ? wagmiChainId
    : domainFromUrl
      ? domainToChainId[domainFromUrl]
      : undefined
  const knownChainIdFromUrl = chains.find((chain) => chain.id === chainIdFromUrl)?.id
  const savingsAccounts = useSavingsAccountRepository({ chainId: knownChainIdFromUrl ?? chainId })

  if (domainFromUrl !== undefined) {
    if (!knownChainIdFromUrl || !savingsTokenFromUrl) {
      throw notFound()
    }

    const accountFromUrl = savingsAccounts
      .all()
      .find((account) => account.savingsToken.symbol.toLowerCase() === savingsTokenFromUrl)
    if (!accountFromUrl) {
      throw notFound()
    }

    return { savingsTokenSymbol: accountFromUrl.savingsToken.symbol, chainId: knownChainIdFromUrl }
  }

  const defaultToken = getDefaultSavingsTokenSymbol(chainId)
  return { savingsTokenSymbol: defaultToken, chainId }
}

export function getDefaultSavingsTokenSymbol(chainId: number): TokenSymbol {
  const { savings } = getChainConfigEntry(chainId)
  return savings?.accounts?.[0]?.savingsToken ?? raise('There are no accounts in config')
}
